import { useEffect, useState } from "react";
import { supabase } from "../../../lib/supabase";
import {
  Building2,
  Pencil,
  X,
  Check,
  Loader2,
  Eye,
  EyeOff,
  RefreshCw,
  PlugZap,
} from "lucide-react";
import { HotelDetailsForm } from "../../onboarding/forms/HotelDetailsForm";
import {
  safeBase64Decode,
  safeBase64Encode,
  CredentialField,
  Connector,
  Credentials,
} from "../../shared/ConnectorCredentialsForm";
import { usePmsCredentialTest } from "../../../hooks/usePmsCredentialTest";
import {
  canRunDraftTest,
  isPmsTestSupported,
} from "../../../lib/pmsCredentialsTest";
import {
  resolveHotelDetailName,
  withSyncedHotelDetailName,
} from "../../../lib/hotelDetailName";

interface HotelDetailsRow {
  id: string;
  company_name?: string | null;
  hotel_name: string;
  category: string | null;
  website: string | null;
  contact_phone: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  hotel_code: string | number | null;
  registration_number?: string | null;
}

interface CredentialsRow {
  id: string;
  hotel_id: string;
  connector_type: string;
  connector_code: string;
  connector_hotel_id: string | null;
  base_url: string | null;
  is_primary: boolean;
  reqParams: string | null;
}

// "pending" is intentionally not exposed to the SaaS — they can only flip
// integrations between active and inactive. (The DB still allows "pending"
// in the CHECK constraint for legacy/internal flows.)
type EditableStatus = "active" | "inactive";

interface IntegrationRow {
  id: string;
  hotel_id: string;
  status: "active" | "inactive" | "pending";
  scopes: string[];
  created_at: string;
  hotel: HotelDetailsRow | null;
  credentials: CredentialsRow[];
}

const STATUS_OPTIONS: EditableStatus[] = ["active", "inactive"];

// Heuristic to decide which credential fields should be rendered as a masked
// "password"-style input with a show/hide toggle.
const isSensitiveField = (key: string): boolean => {
  const k = key.toLowerCase();
  return (
    k.includes("password") ||
    k.includes("secret") ||
    k.includes("token") ||
    k.endsWith("_key") ||
    k === "key" ||
    k === "apikey" ||
    k.includes("api_key")
  );
};

export function WidgetOnboardedHotels() {
  const [rows, setRows] = useState<IntegrationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [editing, setEditing] = useState<IntegrationRow | null>(null);

  useEffect(() => {
    void loadRows();
  }, []);

  const loadRows = async () => {
    setLoading(true);
    setError("");
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Explicit saas_id filter (defense in depth alongside RLS).
      const { data: integrations, error: integrationsError } = await supabase
        .from("integrations")
        .select("id, hotel_id, status, scopes, created_at")
        .eq("saas_id", user.id)
        .eq("onboarded_via_widget", true)
        .order("created_at", { ascending: false });

      if (integrationsError) throw integrationsError;

      const hotelIds = (integrations || []).map((r) => r.hotel_id);
      if (hotelIds.length === 0) {
        setRows([]);
        return;
      }

      const [{ data: hotelsData, error: hotelsError }, { data: credsData, error: credsError }] =
        await Promise.all([
          supabase
            .from("hotel_details")
            .select(
              "id, company_name, hotel_name, category, website, contact_phone, address, postal_code, city, country, hotel_code, registration_number"
            )
            .in("id", hotelIds),
          supabase
            .from("credentials")
            .select(
              "id, hotel_id, connector_type, connector_code, connector_hotel_id, base_url, is_primary, reqParams"
            )
            .in("hotel_id", hotelIds),
        ]);

      if (hotelsError) throw hotelsError;
      if (credsError) throw credsError;

      const hotelById = new Map<string, HotelDetailsRow>();
      (hotelsData || []).forEach((h) => hotelById.set(h.id, h as HotelDetailsRow));
      const credsByHotel = new Map<string, CredentialsRow[]>();
      (credsData || []).forEach((c) => {
        const list = credsByHotel.get(c.hotel_id) || [];
        list.push(c as CredentialsRow);
        credsByHotel.set(c.hotel_id, list);
      });

      const merged: IntegrationRow[] = (integrations || [])
        .map((r) => ({
          id: r.id,
          hotel_id: r.hotel_id,
          status: r.status as IntegrationRow["status"],
          scopes: r.scopes || [],
          created_at: r.created_at,
          hotel: hotelById.get(r.hotel_id) || null,
          credentials: credsByHotel.get(r.hotel_id) || [],
        }))
        // Drop rows we cannot resolve to hotel_details (cross-tenant leak or stale data).
        .filter((r) => r.hotel !== null);

      setRows(merged);
    } catch (err: any) {
      console.error("Error loading widget-onboarded hotels:", err);
      setError(err?.message || "Failed to load widget-onboarded hotels");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="px-6 py-4 text-center text-sm text-gray-500">
            Loading widget-onboarded hotels...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center space-x-2">
          <Building2 className="h-6 w-6 text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-700">Hotels configuration</h2>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {rows.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-sm">
              No hotels have onboarded through your widget yet.
            </p>
            <p className="text-xs mt-2">
              Share the iframe code from <strong>Hotel Onboarding</strong> with your hotels to get started.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hotel
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scopes
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Onboarded
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="font-medium">
                        {resolveHotelDetailName(row.hotel ?? {}) || "—"}
                      </div>
                      {row.hotel?.hotel_code && (
                        <div className="text-xs text-gray-500">Code: {String(row.hotel.hotel_code)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {[row.hotel?.city, row.hotel?.country].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {row.scopes.length === 0 ? (
                        <span className="text-xs text-gray-400">none</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {row.scopes.map((s) => (
                            <span
                              key={s}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700"
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {new Date(row.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <button
                        type="button"
                        onClick={() => setEditing(row)}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <EditIntegrationDrawer
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await loadRows();
          }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: IntegrationRow["status"] }) {
  const styles: Record<IntegrationRow["status"], string> = {
    active: "bg-green-50 text-green-700",
    inactive: "bg-gray-100 text-gray-700",
    pending: "bg-yellow-50 text-yellow-700",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status]}`}
    >
      {status}
    </span>
  );
}

interface EditIntegrationDrawerProps {
  row: IntegrationRow;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

function EditIntegrationDrawer({ row, onClose, onSaved }: EditIntegrationDrawerProps) {
  // Coerce "pending" (legacy) into "inactive" for the SaaS-facing toggle.
  const initialStatus: EditableStatus = row.status === "active" ? "active" : "inactive";
  const [status, setStatus] = useState<EditableStatus>(initialStatus);
  const [hotelDetails, setHotelDetails] = useState({
    hotel_name: resolveHotelDetailName(row.hotel ?? {}),
    category: row.hotel?.category || "",
    website: row.hotel?.website || "",
    contact_phone: row.hotel?.contact_phone || "",
    address: row.hotel?.address || "",
    postal_code: row.hotel?.postal_code || "",
    city: row.hotel?.city || "",
    country: row.hotel?.country || "",
    registration_number: row.hotel?.registration_number || "",
  });
  const primary = row.credentials.find((c) => c.is_primary) || row.credentials[0];

  const [credentialEdits, setCredentialEdits] = useState({
    base_url: primary?.base_url || "",
    connector_hotel_id: primary?.connector_hotel_id || "",
  });
  const [originalCredentialEdits, setOriginalCredentialEdits] = useState({
    base_url: primary?.base_url || "",
    connector_hotel_id: primary?.connector_hotel_id || "",
  });

  // Connector schema (the list of credential fields, e.g. username/password/api_key)
  // for the primary credential's connector. Loaded once the drawer opens.
  const [connectorFields, setConnectorFields] = useState<CredentialField[] | null>(null);
  const [isCloudified, setIsCloudified] = useState<boolean>(false);
  // Decoded credential values, keyed by field name. Editable.
  const [credentialFieldValues, setCredentialFieldValues] = useState<
    Record<string, string>
  >({});
  // Original snapshot so we can detect rotations and skip the noisy network call
  // when nothing was actually rotated.
  const [originalCredentialFieldValues, setOriginalCredentialFieldValues] =
    useState<Record<string, string>>({});
  const [credentialFieldsLoading, setCredentialFieldsLoading] = useState(false);
  const [credentialFieldsError, setCredentialFieldsError] = useState<string>("");
  const [revealedFields, setRevealedFields] = useState<Record<string, boolean>>({});

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const {
    testLoading,
    testResult,
    testError,
    runDraftTest,
    clearTestResult,
  } = usePmsCredentialTest();

  const hotelCode = row.hotel?.hotel_code ? String(row.hotel.hotel_code) : "";

  useEffect(() => {
    if (!primary) {
      setConnectorFields(null);
      return;
    }
    void loadConnectorAndCredentials(primary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConnectorAndCredentials = async (cred: CredentialsRow) => {
    setCredentialFieldsLoading(true);
    setCredentialFieldsError("");
    try {
      const { data: connectorData, error: connectorErr } = await supabase
        .from("connectors")
        .select("code, name, cloudified, credentials")
        .ilike("code", cred.connector_code)
        .maybeSingle();

      if (connectorErr) throw connectorErr;

      const fields: CredentialField[] = Array.isArray(connectorData?.credentials)
        ? (connectorData!.credentials as CredentialField[])
        : [];
      setConnectorFields(fields);
      setIsCloudified(connectorData?.cloudified === true);

      // Decode existing reqParams (base64-encoded JSON).
      // Shape: { credentials?: Record<string,string>, sftp?: {...} }.
      const initial: Record<string, string> = {};
      if (cred.reqParams) {
        const decoded = safeBase64Decode(cred.reqParams);
        const decodedCreds = decoded?.credentials || {};
        for (const f of fields) {
          const v = decodedCreds[f.key];
          initial[f.key] = typeof v === "string" ? v : "";
        }
      } else {
        for (const f of fields) initial[f.key] = "";
      }
      setCredentialFieldValues(initial);
      setOriginalCredentialFieldValues({ ...initial });
      setOriginalCredentialEdits({
        base_url: cred.base_url || "",
        connector_hotel_id: cred.connector_hotel_id || "",
      });
    } catch (err: any) {
      console.error("Error loading connector definition:", err);
      setCredentialFieldsError(
        err?.message || "Failed to load connector definition for credential rotation"
      );
      setConnectorFields([]);
    } finally {
      setCredentialFieldsLoading(false);
    }
  };

  const toggleReveal = (key: string) =>
    setRevealedFields((prev) => ({ ...prev, [key]: !prev[key] }));

  const credentialsChanged = (): boolean => {
    if (!connectorFields) return false;
    for (const f of connectorFields) {
      if ((credentialFieldValues[f.key] || "") !== (originalCredentialFieldValues[f.key] || "")) {
        return true;
      }
    }
    return false;
  };

  const pmsFormDirty = (): boolean => {
    if (credentialEdits.base_url !== originalCredentialEdits.base_url) {
      return true;
    }
    if (credentialEdits.connector_hotel_id !== originalCredentialEdits.connector_hotel_id) {
      return true;
    }
    return credentialsChanged();
  };

  const buildConnectorForTest = (): Connector | null => {
    if (!primary) return null;
    return {
      id: primary.id,
      code: primary.connector_code,
      name: primary.connector_code,
      type: primary.connector_type,
      status: "active",
      cloudified: isCloudified,
      credentials: connectorFields ?? undefined,
    };
  };

  const buildCredentialsForTest = (): Credentials => ({
    base_url: credentialEdits.base_url,
    connector_hotel_id: credentialEdits.connector_hotel_id,
    ...credentialFieldValues,
  });

  const canRunTest = (): boolean => {
    if (!primary || isCloudified || !hotelCode) return false;
    if (!isPmsTestSupported(primary.connector_code)) return false;
    return canRunDraftTest(buildConnectorForTest(), buildCredentialsForTest());
  };

  const handleTestConnection = () => {
    if (!primary || !hotelCode) return;

    // Always draft-test with values loaded from Supabase. Saved-test mode looks up
    // credentials via the backend using the caller's JWT + hotel-code; SaaS users
    // managing widget-onboarded hotels only have credentials in our DB (reqParams).
    runDraftTest({
      connector: buildConnectorForTest(),
      credentials: buildCredentialsForTest(),
      hotelCode,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // 1) integrations.status
      if (status !== row.status) {
        const { error: integrationErr } = await supabase
          .from("integrations")
          .update({ status })
          .eq("id", row.id)
          .eq("saas_id", user.id)
          .eq("onboarded_via_widget", true);
        if (integrationErr) throw integrationErr;
      }

      // 2) hotel_details.* (only writable fields the SaaS can set)
      if (row.hotel) {
        const { error: hotelErr } = await supabase
          .from("hotel_details")
          .update({
            ...withSyncedHotelDetailName(hotelDetails.hotel_name),
            category: hotelDetails.category,
            website: hotelDetails.website || null,
            contact_phone: hotelDetails.contact_phone || null,
            address: hotelDetails.address || null,
            postal_code: hotelDetails.postal_code || null,
            city: hotelDetails.city || null,
            country: hotelDetails.country || null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", row.hotel.id);
        if (hotelErr) throw hotelErr;
      }

      // 3) credentials (primary only): base_url, connector_hotel_id, and optionally
      //    rotated authentication fields re-encoded into reqParams.
      if (primary) {
        const updatePayload: Record<string, unknown> = {
          base_url: credentialEdits.base_url || null,
          connector_hotel_id: credentialEdits.connector_hotel_id || null,
          updated_at: new Date().toISOString(),
        };

        if (credentialsChanged()) {
          // Preserve any non-credential keys already encoded (e.g. cloudified sftp block).
          const previous = primary.reqParams ? safeBase64Decode(primary.reqParams) || {} : {};
          const nextCredsBlock: Record<string, string> = {};
          (connectorFields || []).forEach((f) => {
            const v = credentialFieldValues[f.key];
            if (typeof v === "string" && v.length > 0) {
              nextCredsBlock[f.key] = v;
            }
          });
          const nextReqParams = {
            ...previous,
            credentials: nextCredsBlock,
          };
          updatePayload.reqParams = safeBase64Encode(nextReqParams);
        }

        const { error: credErr } = await supabase
          .from("credentials")
          .update(updatePayload)
          .eq("id", primary.id);
        if (credErr) throw credErr;
      }

      await onSaved();
    } catch (err: any) {
      console.error("Error saving widget integration:", err);
      setError(err?.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <button
        type="button"
        aria-label="Close edit panel"
        className="absolute inset-0 bg-gray-900/50"
        onClick={onClose}
      />
      <div className="ml-auto w-full max-w-2xl bg-white shadow-xl overflow-y-auto h-full relative z-10">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">
            Edit integration — {resolveHotelDetailName(row.hotel ?? {}) || "Hotel"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600 whitespace-pre-wrap">{error}</p>
            </div>
          )}

          {/* Status */}
          <section>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Integration status</h4>
            <div className="flex items-center space-x-2">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`px-3 py-1 rounded-md text-sm border ${
                    status === s
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </section>

          {/* Scopes (read-only) */}
          <section>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">
              Scopes <span className="text-xs font-normal text-gray-500">(read-only — managed in Functional Scope)</span>
            </h4>
            {row.scopes.length === 0 ? (
              <p className="text-xs text-gray-400">None.</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {row.scopes.map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700"
                  >
                    {s}
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* Hotel details */}
          <section>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Hotel details</h4>
            <HotelDetailsForm
              hotelDetails={hotelDetails}
              setHotelDetails={setHotelDetails}
            />
          </section>

          {/* PMS credentials */}
          <section>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">PMS credentials</h4>
            {primary ? (
              <div className="space-y-3">
                <div>
                  <label htmlFor="cred-connector" className="form-label">Connector</label>
                  <input
                    id="cred-connector"
                    type="text"
                    value={primary.connector_code}
                    readOnly
                    className="form-input bg-gray-50 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label htmlFor="cred-hotel-id" className="form-label">Hotel identifier (within PMS)</label>
                  <input
                    id="cred-hotel-id"
                    type="text"
                    value={credentialEdits.connector_hotel_id}
                    onChange={(e) => {
                      clearTestResult();
                      setCredentialEdits((prev) => ({
                        ...prev,
                        connector_hotel_id: e.target.value,
                      }));
                    }}
                    className="form-input"
                  />
                </div>
                <div>
                  <label htmlFor="cred-base-url" className="form-label">Base URL</label>
                  <input
                    id="cred-base-url"
                    type="text"
                    value={credentialEdits.base_url}
                    onChange={(e) => {
                      clearTestResult();
                      setCredentialEdits((prev) => ({
                        ...prev,
                        base_url: e.target.value,
                      }));
                    }}
                    className="form-input"
                  />
                </div>

                {/* Authentication / rotation */}
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-sm font-semibold text-gray-700 flex items-center">
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                      Authentication
                    </h5>
                    {credentialsChanged() && (
                      <button
                        type="button"
                        onClick={() => setCredentialFieldValues({ ...originalCredentialFieldValues })}
                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                      >
                        Reset to current values
                      </button>
                    )}
                  </div>

                  {credentialFieldsLoading ? (
                    <div className="flex items-center text-xs text-gray-500">
                      <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      Loading credential schema...
                    </div>
                  ) : credentialFieldsError ? (
                    <p className="text-xs text-red-600">{credentialFieldsError}</p>
                  ) : isCloudified ? (
                    <p className="text-xs text-gray-500">
                      This connector is cloudified — credentials are managed by the cloud (SFTP). Nothing to rotate here.
                    </p>
                  ) : !connectorFields || connectorFields.length === 0 ? (
                    <p className="text-xs text-gray-500">
                      The connector definition exposes no rotatable credential fields.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {connectorFields.map((field) => {
                        const sensitive = isSensitiveField(field.key);
                        const inputId = `cred-field-${field.key}`;
                        const revealed = revealedFields[field.key] === true;
                        return (
                          <div key={field.key}>
                            <label htmlFor={inputId} className="form-label">
                              {field.key}
                            </label>
                            <div className="relative">
                              <input
                                id={inputId}
                                type={sensitive && !revealed ? "password" : "text"}
                                autoComplete="off"
                                value={credentialFieldValues[field.key] || ""}
                                onChange={(e) => {
                                  clearTestResult();
                                  setCredentialFieldValues((prev) => ({
                                    ...prev,
                                    [field.key]: e.target.value,
                                  }));
                                }}
                                placeholder={field.example || ""}
                                className={`form-input ${sensitive ? "pr-10 font-mono" : ""}`}
                              />
                              {sensitive && (
                                <button
                                  type="button"
                                  aria-label={revealed ? "Hide value" : "Show value"}
                                  onClick={() => toggleReveal(field.key)}
                                  className="absolute inset-y-0 right-0 px-3 text-gray-400 hover:text-gray-600"
                                >
                                  {revealed ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                            </div>
                            {field.description && (
                              <p className="mt-1 text-xs text-gray-500">{field.description}</p>
                            )}
                          </div>
                        );
                      })}
                      <p className="text-xs text-gray-500">
                        Replace any value to rotate it. The new credentials are encrypted at rest in <code className="px-1 py-0.5 bg-gray-100 rounded">credentials.reqParams</code> and take effect immediately on save.
                      </p>

                      {/* Test connection */}
                      <div className="border-t border-gray-200 pt-3 mt-1 space-y-3">
                        {isPmsTestSupported(primary.connector_code) ? (
                          <button
                            type="button"
                            onClick={handleTestConnection}
                            disabled={testLoading || !canRunTest()}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {testLoading ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Testing…
                              </>
                            ) : (
                              <>
                                <PlugZap className="h-4 w-4 mr-2" />
                                Test connection
                              </>
                            )}
                          </button>
                        ) : (
                          <div>
                            <button
                              type="button"
                              disabled
                              title="Coming soon for this PMS"
                              className="inline-flex items-center px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-400 bg-gray-50 cursor-not-allowed"
                            >
                              <PlugZap className="h-4 w-4 mr-2" />
                              Test connection
                            </button>
                            <p className="mt-1 text-xs text-gray-500">
                              Coming soon for this PMS
                            </p>
                          </div>
                        )}

                        {pmsFormDirty() && (
                          <p className="text-xs text-gray-500">
                            Testing unsaved changes before save.
                          </p>
                        )}

                        {testError && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-700">{testError}</p>
                          </div>
                        )}

                        {testResult && !testError && (
                          <div
                            className={`p-3 border rounded-md ${
                              testResult.success
                                ? "bg-green-50 border-green-200"
                                : "bg-red-50 border-red-200"
                            }`}
                          >
                            <p
                              className={`text-sm ${
                                testResult.success ? "text-green-800" : "text-red-700"
                              }`}
                            >
                              {testResult.message}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                No PMS credentials are stored for this hotel yet.
              </p>
            )}
          </section>

          <div className="pt-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-md text-sm text-white font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
